import type { Fighter } from '@/game/fighters/fighter';
import type { InputState } from '@/shared/types';

export type AIDifficulty = 'easy' | 'medium' | 'hard' | 'demo';

export class AIController {
  private reactionDelay = 0;
  private nextAction: Partial<InputState> = {};
  private actionCooldown = 0;

  constructor(private difficulty: AIDifficulty = 'easy') {}

  update(self: Fighter, opponent: Fighter): InputState {
    const input: InputState = {
      left: false,
      right: false,
      up: false,
      down: false,
      attack1: false,
      attack2: false,
      special: false,
      block: false,
    };

    if (self.state === 'ko' || self.state === 'hit') return input;

    if (this.actionCooldown > 0) this.actionCooldown--;

    const dx = opponent.x - self.x;
    const distance = Math.abs(dx);

    if (this.reactionDelay > 0) {
      this.reactionDelay--;
      return { ...input, ...this.nextAction };
    }

    if (this.difficulty === 'demo') {
      return this.demoUpdate(self, opponent, input, dx, distance);
    }

    const reactionFrames = this.difficulty === 'easy' ? 30 : this.difficulty === 'medium' ? 15 : 5;

    if (opponent.isAttacking && distance < 180) {
      if (Math.random() < this.blockChance) {
        this.scheduleAction({ block: true }, reactionFrames);
        return input;
      }
    }

    if (distance > 225) {
      if (dx > 0) input.right = true;
      else input.left = true;

      if (Math.random() < 0.01) input.up = true;
    } else if (distance < 150) {
      const roll = Math.random();
      if (roll < 0.05) {
        input.attack1 = true;
      } else if (roll < 0.08) {
        input.attack2 = true;
      } else if (roll < 0.09 && self.health < 40) {
        input.special = true;
      }
    } else {
      const roll = Math.random();
      if (roll < 0.03) {
        input.attack1 = true;
      } else if (roll < 0.04) {
        if (dx > 0) input.right = true;
        else input.left = true;
      }
    }

    return input;
  }

  private demoUpdate(
    self: Fighter,
    opponent: Fighter,
    input: InputState,
    dx: number,
    distance: number,
  ): InputState {
    if (opponent.isAttacking && distance < 195) {
      if (Math.random() < 0.4) {
        input.block = true;
        return input;
      }
      if (Math.random() < 0.3 && self.isGrounded) {
        input.up = true;
        return input;
      }
    }

    if (distance > 270) {
      if (dx > 0) input.right = true;
      else input.left = true;

      if (Math.random() < 0.04 && self.isGrounded) {
        input.up = true;
      }
    } else if (distance > 150) {
      if (dx > 0) input.right = true;
      else input.left = true;

      if (this.actionCooldown === 0 && Math.random() < 0.12) {
        input.attack2 = true;
        this.actionCooldown = 10;
      }
    } else {
      if (this.actionCooldown > 0) return input;

      const roll = Math.random();
      if (roll < 0.15) {
        input.attack1 = true;
        this.actionCooldown = 8;
      } else if (roll < 0.25) {
        input.attack2 = true;
        this.actionCooldown = 10;
      } else if (roll < 0.3) {
        input.special = true;
        this.actionCooldown = 20;
      } else if (roll < 0.35 && self.isGrounded) {
        input.up = true;
      } else if (roll < 0.4) {
        if (dx > 0) input.left = true;
        else input.right = true;
      }
    }

    return input;
  }

  private get blockChance(): number {
    switch (this.difficulty) {
      case 'easy':
        return 0.2;
      case 'medium':
        return 0.5;
      case 'hard':
        return 0.8;
      case 'demo':
        return 0.4;
    }
  }

  private scheduleAction(action: Partial<InputState>, delay: number): void {
    this.reactionDelay = delay;
    this.nextAction = action;
  }
}
